import {
    PlainStudenCardContainer,
    ProfileContainer,
    ProfileStats,
    ProfileStatsDivider,
    ProfileSummary,
    StudentImage,
} from './PlainStudentCard.styled';
import {studentGridThemeColors, studentThemeContrastColors} from '@styles/themes';
import StudentStatsSummary from '@components/StudentStatsSummary';
import {memo, MouseEventHandler, useEffect} from 'react';
import {useStore} from '@store/appStore';
import { shallow } from 'zustand/shallow';
import useVisibility from '@hooks/useVisibility';
import {AttendanceMap, AttendanceStatus} from '@models/AttendanceType';
import persistentStore from '@store/persistentStore';
import {normalizeName} from '@utils/textTools';
import {animated} from 'react-spring';
import StudentThumbnail from '@components/StudentThumbnail';
import {useSearch} from '@providers/SearchProvider';
import SkeletonAnim from '@components/Animation/SkeletonAnim';
import PlaceholderPerson from '@components/PlaceholderPerson';
import {Heading3, Shadow, TinyText} from '@styles/reusable';
import {ViewColoring, ViewIndicators} from '@models/Accessibility';
import AttendanceIcon from '../../AttendanceIcon';
import HaveNote from '@components/Buttons/HaveNote';
import RegMethod from "@components/Buttons/RegistrationMethod";
import { calculatePercentage } from '@utils/percentageTools';
import StatusBar from '@components/Buttons/StatusBar';

interface Props {
  content: any;
  info?: boolean;
  className?: string;
  onClick?: (sisId, duration?) => void;
  index?: number;
  currentSubject?: any;
  attendance?: any;
  style?: any;
}

const AnimatedStudentCardContainer = animated(PlainStudenCardContainer);





export const PlainStudentCard = ({ content, style, info = true, onClick, index, className = '' }: Props) => {
  const { username, firstname, uni_name, sis_id, lastname, displayname, type, total_percentage, loading, course_percentage, id, notePresent, noteUnreadCount, initial_Method, duration, course_absence_duration, course_absence_lessons, course_duration, course_lessons, total_absence_duration, total_absence_lessons, total_duration,total_lessons, merit, note, tag_ids } = content;
  const { setRegistrationIndex, registrationIndex, settings, schedule, scheduleIndex, systemSettings } = useStore(
    (state) => ({
      setRegistrationIndex: state.setRegistrationIndex,
      registrationIndex: state.registrationIndex,
      schedule: state.schedule,
      scheduleIndex: state.scheduleIndex,
      settings: state.settings,
      systemSettings: state.systemSettings,
    }),
    shallow,
  );


  const { registrationQuery } = useSearch();
  const { privacyEnabled, viewColoring, viewIndicators } = persistentStore();
  const isSelected = registrationIndex === index;
  const [isVisible, ref] = useVisibility<HTMLDivElement>(-30);
  const currentSchedule = schedule.data ? schedule.data[scheduleIndex] : { name: '' };
  const name = firstname ? firstname : displayname ? displayname : '';
  const normalizedName = normalizeName(name);
  const combinedSchedule = currentSchedule?.name?.toString()?.includes(' + ');

  let defaultAttendanceSetting = systemSettings.find(s => s.APP_Setting_ID === 19)

  const attendanceStatus = AttendanceMap[type] || 'default';
  const status = defaultAttendanceSetting && defaultAttendanceSetting.value === "Present" && type === -1?AttendanceMap[1]:attendanceStatus


  const coloring = viewColoring === ViewColoring.CONTRAST ? studentThemeContrastColors[status] : studentGridThemeColors[status] ;

  const scrollWinTo = () => {
    const element = ref.current;
    const offset = 170;
    const bodyRect = document.body.getBoundingClientRect().top;
    const elementRect = element.getBoundingClientRect().top;
    const elementPosition = elementRect - bodyRect;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    if (registrationIndex === 0) {
      return;
    }

    if (!isVisible && isSelected) {
      scrollWinTo();
    }
  }, [registrationIndex, isSelected]);

  useEffect(() => {
    if (registrationQuery.forType === 'STUDENT') {
      if (username !== registrationQuery.forVal) {
        return;
      }
      setRegistrationIndex(index);
    }
  }, []);

  const handleClick = (e) => {
    if (registrationQuery.forType === 'STUDENT') {
      if (username !== registrationQuery.forVal) {
        e.preventDefault();
        return;
      }
    }
    onClick(sis_id, type === 3?duration:null);
  };

  return (
    <AnimatedStudentCardContainer
      onClick={onClick ? (e) => handleClick(e) : null}
      selected={isSelected}
      className={className}
      blurred={registrationQuery.forType === 'STUDENT' && registrationQuery.forVal !== username}
      // @ts-ignore
      css={coloring}
      style={style}
      ref={ref}
      backgroundColor={viewIndicators === ViewIndicators.ICONS}
    >
      <StudentImage>
        <StudentThumbnail regView={true} font={"2em"} name={normalizedName + " " + (lastname && normalizeName(lastname))} src={uni_name ? uni_name : username} />
        
        {viewIndicators === ViewIndicators.COLOR? "" : <AttendanceIcon attendanceStatus={status} isContrast={viewColoring === ViewColoring.CONTRAST || viewIndicators === ViewIndicators.COLOR_AND_CONTRAST_ICONS} />}
      </StudentImage>
      <ProfileContainer contrast={viewColoring === ViewColoring.CONTRAST || viewIndicators === ViewIndicators.ICONS} attendanceStatus={status} privacyEnabled={privacyEnabled}>
        <ProfileSummary>
          <Heading3 bold={true}>{normalizedName}</Heading3>
          <TinyText>{lastname && normalizeName(lastname)}</TinyText>
        </ProfileSummary>
        {!privacyEnabled && settings?.ENABLE_PRIVACY_SETTING == 'true' && (
          <ProfileStats>
            <ProfileStatsDivider />
            {info && (
              <>
                <StudentStatsSummary
                  combinedSchedule={combinedSchedule}
                  loading={loading}
                  absence={merit?calculatePercentage(total_duration, total_absence_duration) :total_percentage}
                  courseAbsence={merit?calculatePercentage(course_duration, course_absence_duration):course_percentage}
                />
              </>
            )}
          </ProfileStats>
        )}
      </ProfileContainer>
      <Shadow size={3} />
      <StatusBar note={{interact:(registrationQuery.forType === 'STUDENT' && registrationQuery.forVal !== username), id: sis_id, show:notePresent, notecount:noteUnreadCount}} reason={{type: type, note:note, tag_ids: tag_ids}}>
        <RegMethod studentUsername={username} studentId={sis_id} reservationId={schedule?.data[scheduleIndex]?.id} show={initial_Method !== null && initial_Method !== 0} registrationMethod={initial_Method} />
      </StatusBar>
    </AnimatedStudentCardContainer>
  );
};

export const MockPlainStudentCard = () => {
  return (
    <PlainStudenCardContainer blurred={false} selected={false} mock={true}>
      <StudentImage>
        <PlaceholderPerson />
      </StudentImage>
      <ProfileContainer contrast={false} attendanceStatus={AttendanceStatus.Unknown} privacyEnabled={false}>
        <ProfileSummary>
          <h3>
            <SkeletonAnim height={15} width={60} />
          </h3>
          <span>
            <SkeletonAnim height={13} width={120} />
          </span>
        </ProfileSummary>
      </ProfileContainer>
    </PlainStudenCardContainer>
  );
};

export default memo(PlainStudentCard);
